import { useEffect, useState } from "react";
import { Loader2, Download, TrendingUp, Wallet, Package } from "lucide-react";
import { api, formatRp, formatDate, statusMeta, getStatusDisplay } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const months = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];

export default function ReportsPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/reports/monthly", { params: { year, month } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [year, month]);

  const exportCSV = () => {
    if (!data) return;
    const rows = [
      ["No. Tiket", "Tanggal", "Pelanggan", "HP", "Jenis", "Merek", "Keluhan", "Status", "Estimasi", "Final"],
      ...data.services.map((s) => [
        s.ticket_no,
        formatDate(s.created_at),
        s.customer_name,
        s.customer_phone,
        s.item_type,
        s.item_brand,
        (s.complaint || "").replace(/\n/g, " "),
        s.status,
        s.estimated_cost,
        s.final_cost,
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `laporan-${year}-${String(month).padStart(2, "0")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 4; y--) years.push(y);

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
          Laporan Bulanan
        </h1>
        <p className="text-sm text-slate-500 mt-1">Rekap aktivitas & pendapatan per bulan.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 flex flex-wrap gap-3 items-end">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Bulan</div>
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-40" data-testid="report-month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((m, i) => (
                <SelectItem key={i} value={String(i + 1)}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Tahun</div>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="report-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto">
          <Button onClick={exportCSV} variant="outline" className="gap-2" data-testid="export-csv-button">
            <Download className="w-4 h-4" /> Export CSV
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <Package className="w-4 h-4" /> Total Order
              </div>
              <div className="mt-2 font-display text-3xl font-bold text-slate-900">{data.total}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <TrendingUp className="w-4 h-4" /> Selesai
              </div>
              <div className="mt-2 font-display text-3xl font-bold text-emerald-700">
                {(data.by_status.selesai || 0) + (data.by_status.diambil || 0)}
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <Loader2 className="w-4 h-4" /> Proses
              </div>
              <div className="mt-2 font-display text-3xl font-bold text-blue-700">
                {(data.by_status.masuk || 0) + (data.by_status.proses || 0)}
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <Wallet className="w-4 h-4" /> Pendapatan
              </div>
              <div className="mt-2 font-display text-2xl font-bold text-slate-900">
                {formatRp(data.revenue)}
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="font-display font-semibold text-slate-900 mb-3">Distribusi Jenis Barang</h3>
              {Object.keys(data.by_item).length === 0 ? (
                <div className="text-sm text-slate-500">Belum ada data.</div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(data.by_item)
                    .sort((a, b) => b[1] - a[1])
                    .map(([item, count]) => {
                      const pct = Math.round((count / data.total) * 100);
                      return (
                        <div key={item}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-700">{item}</span>
                            <span className="text-slate-500 text-xs">{count} · {pct}%</span>
                          </div>
                          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full bg-blue-600" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="font-display font-semibold text-slate-900 mb-3">Kontribusi Teknisi</h3>
              {Object.keys(data.by_tech).length === 0 ? (
                <div className="text-sm text-slate-500">Belum ada order yang selesai.</div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(data.by_tech)
                    .sort((a, b) => b[1] - a[1])
                    .map(([tech, count]) => (
                      <div
                        key={tech}
                        className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 border border-slate-100"
                      >
                        <span className="text-sm text-slate-800">{tech}</span>
                        <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                          {count} selesai
                        </span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <h3 className="font-display font-semibold text-slate-900">Detail Transaksi</h3>
            </div>
            {data.services.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-500">Tidak ada data pada bulan ini.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="px-4 py-3 font-semibold">Tiket</th>
                      <th className="px-4 py-3 font-semibold">Pelanggan</th>
                      <th className="px-4 py-3 font-semibold">Barang</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold text-right">Biaya</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.services.map((s) => (
                      <tr key={s.id}>
                        <td className="px-4 py-2 font-mono text-xs">{s.ticket_no}</td>
                        <td className="px-4 py-2">{s.customer_name}</td>
                        <td className="px-4 py-2 text-slate-600">
                          {s.item_brand} <span className="text-xs">({s.item_type})</span>
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${getStatusDisplay(s).cls}`}
                          >
                            {getStatusDisplay(s).label}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right font-semibold text-slate-800">
                          {formatRp(s.final_cost || s.estimated_cost)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
